import { z } from "zod";
import { BusinessDate, Instant, Name, Note, Version } from "./common.js";
import { Id } from "./ids.js";

/**
 * Purchasing and suppliers (requirements §8.6, §10.4): purchase request → manager approval → receipt in the
 * warehouse with the supplier's invoice; debts and payments to each supplier. Money is integer rial.
 */

const RialAmount = z.number().int().nonnegative();
const PositiveRial = z.number().int().positive();

export const SupplierInput = z.object({
  name: Name,
  phone: z.string().trim().max(20).default(""),
  economicCode: z.string().trim().max(20).default(""),
  address: Note.default(""),
  note: Note.default(""),
});
export type SupplierInput = z.infer<typeof SupplierInput>;

export const Supplier = SupplierInput.extend({
  id: Id,
  version: Version,
  /** Σ bills − Σ payments, integer rial; positive means we owe the supplier. */
  balanceRial: z.number().int(),
});
export type Supplier = z.infer<typeof Supplier>;

export const PurchaseRequestStatus = z.enum([
  "draft",
  "requested",
  "approved",
  "declined",
  "received",
  "cancelled",
]);
export type PurchaseRequestStatus = z.infer<typeof PurchaseRequestStatus>;

export const PurchaseRequestLineInput = z.object({
  itemId: Id,
  quantityMilli: z.number().int().positive(),
  expectedUnitPriceRial: RialAmount.optional(),
  note: Note.optional(),
});
export type PurchaseRequestLineInput = z.infer<typeof PurchaseRequestLineInput>;

export const PurchaseRequestInput = z.object({
  warehouseId: Id,
  supplierId: Id.optional(),
  neededBy: BusinessDate,
  note: Note.default(""),
  lines: z.array(PurchaseRequestLineInput).min(1).max(200),
});
export type PurchaseRequestInput = z.infer<typeof PurchaseRequestInput>;

export const PurchaseRequestPatch = PurchaseRequestInput.partial();
export type PurchaseRequestPatch = z.infer<typeof PurchaseRequestPatch>;

export const PurchaseRequest = z.object({
  id: Id,
  version: Version,
  status: PurchaseRequestStatus,
  warehouseId: Id,
  supplierId: Id.nullable(),
  neededBy: BusinessDate,
  note: z.string(),
  lines: z.array(PurchaseRequestLineInput.extend({ id: Id })),
  /** Σ quantity × expected price, rounded half up once per line (quantities are in thousandths). */
  expectedTotalRial: z.number().int().nonnegative(),
  requestedBy: Id.nullable(),
  decidedBy: Id.nullable(),
  decidedAt: Instant.nullable(),
  decisionNote: z.string(),
  receiptVoucherId: Id.nullable(),
  createdAt: Instant,
});
export type PurchaseRequest = z.infer<typeof PurchaseRequest>;

export const PurchaseDecisionInput = z.object({
  version: Version,
  decision: z.enum(["approve", "decline"]),
  note: Note.default(""),
});
export type PurchaseDecisionInput = z.infer<typeof PurchaseDecisionInput>;

export const ReceivePurchaseInput = z.object({
  version: Version,
  date: BusinessDate,
});
export type ReceivePurchaseInput = z.infer<typeof ReceivePurchaseInput>;

export const SupplierBillInput = z.object({
  supplierId: Id,
  /** The supplier's own invoice number; unique per supplier among live bills. */
  billNumber: z.string().trim().min(1).max(40),
  billDate: BusinessDate,
  totalRial: PositiveRial,
  requestId: Id.optional(),
  note: Note.default(""),
});
export type SupplierBillInput = z.infer<typeof SupplierBillInput>;

export const SupplierPaymentMethod = z.enum(["cash", "card", "transfer", "cheque"]);
export type SupplierPaymentMethod = z.infer<typeof SupplierPaymentMethod>;

export const SupplierPaymentInput = z
  .object({
    supplierId: Id,
    paidOn: BusinessDate,
    amountRial: PositiveRial,
    method: SupplierPaymentMethod,
    reference: z.string().trim().max(60).default(""),
    chequeNumber: z.string().trim().max(30).optional(),
    chequeDueDate: BusinessDate.optional(),
    note: Note.default(""),
  })
  .refine((p) => p.method !== "cheque" || (p.chequeNumber !== undefined && p.chequeDueDate !== undefined), {
    message: "برای چک، شماره و تاریخ سررسید لازم است",
    path: ["chequeNumber"],
  });
export type SupplierPaymentInput = z.infer<typeof SupplierPaymentInput>;

export const VoidInput = z.object({ version: Version, reason: Note.pipe(z.string().min(3)) });
export type VoidInput = z.infer<typeof VoidInput>;

export const SupplierStatementEntry = z.object({
  date: BusinessDate,
  kind: z.enum(["bill", "payment"]),
  id: Id,
  reference: z.string(),
  debitRial: z.number().int().nonnegative(),
  creditRial: z.number().int().nonnegative(),
  balanceRial: z.number().int(),
});
export type SupplierStatementEntry = z.infer<typeof SupplierStatementEntry>;

export const SupplierStatement = z.object({
  supplierId: Id,
  from: BusinessDate,
  to: BusinessDate,
  openingRial: z.number().int(),
  entries: z.array(SupplierStatementEntry),
  closingRial: z.number().int(),
});
export type SupplierStatement = z.infer<typeof SupplierStatement>;
